import React, { useState, useEffect, useRef } from 'react';
import { BiBot, BiX, BiRefresh, BiChevronRight, BiDownload } from 'react-icons/bi';
import { BsSend } from 'react-icons/bs';
import { jsPDF } from 'jspdf';

import axios from '../../../axios';

// ---------------------------------------------------------------------------
// Ask Trivvy — slide-over chat drawer for the User Chats overview.
//
// Hooks up to the AI Query backend (POST /ai-query/v1/ask, then
// POST /ai-query/v1/:requestId/confirm). The backend is human-in-the-loop by
// design: ask only ever produces a PENDING plan preview, and nothing executes
// until the admin explicitly confirms it in the plan card below.
// ---------------------------------------------------------------------------

const AI_QUERY_ASK = '/ai-query/v1/ask';
const aiQueryConfirmUrl = (requestId) => '/ai-query/v1/' + requestId + '/confirm';
const aiQueryResultsUrl = (requestId) => '/ai-query/v1/' + requestId + '/results';

const SUGGESTIONS = [
    'What new issues surfaced this week?',
    'Which hashtags are trending negative?',
    'Which participants need follow-up?',
];

const getErrorText = (err, fallback) => {
    var data = err && err.response && err.response.data;
    if (data && data.error) {
        return data.detail ? (data.error + ' ' + data.detail) : data.error;
    }
    if (err && err.message && !err.response) return 'Network error: ' + err.message;
    return fallback || 'Something went wrong. Please try again.';
};

let msgSeq = 0;
const nextId = () => 'atv-' + (++msgSeq) + '-' + Date.now();

const AskTrivvyDrawer = ({ open, onClose, user }) => {
    const firstName = (user && user.profile && user.profile.firstName) || '';
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    // requestId of the latest unanswered clarification question — the next
    // message the admin sends is its answer, so it goes out as followUpTo.
    const [pendingClarification, setPendingClarification] = useState(null);
    // True while the clear-chat alert box is showing.
    const [confirmingClear, setConfirmingClear] = useState(false);
    const bodyRef = useRef(null);
    const inputRef = useRef(null);

    // --- open/close behavior: focus, escape, backdrop scroll lock ---
    useEffect(() => {
        if (open) {
            document.body.classList.add('atv-lock');
            var t = setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 350);
            var onKey = (e) => {
                if (e.key === 'Escape') {
                    if (confirmingClear) setConfirmingClear(false);
                    else onClose();
                }
            };
            window.addEventListener('keydown', onKey);
            return () => {
                document.body.classList.remove('atv-lock');
                clearTimeout(t);
                window.removeEventListener('keydown', onKey);
            };
        }
        return undefined;
    }, [open, onClose, confirmingClear]);

    // --- keep the thread pinned to the latest message ---
    useEffect(() => {
        if (bodyRef.current) {
            bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
        }
    }, [messages, open]);

    // Clear-chat alert box (styled in-app modal, not window.confirm).
    const requestClearConversation = () => {
        if (busy || messages.length === 0) return;
        setConfirmingClear(true);
    };
    const clearConversation = () => {
        setConfirmingClear(false);
        setMessages([]);
        setInput('');
        setPendingClarification(null);
    };

    // ------------------------------------------------------------------
    // Download — one-click PDF of the current drawer session, generated
    // directly with jsPDF (page-by-page text: fast, no page freeze,
    // selectable/searchable text, any session size). Requires the `jspdf`
    // npm package.
    // ------------------------------------------------------------------
    const downloadSessionPdf = () => {
        if (busy || messages.length === 0) return;
        try {
            buildSessionPdf(messages);
        } catch (e) {
            pushMessage({ id: nextId(), role: 'assistant', kind: 'error', text: 'Could not generate the PDF. Please try again.' });
        }
    };

    const pushMessage = (msg) => setMessages((prev) => prev.concat([msg]));

    const replaceMessage = (id, msg) =>
        setMessages((prev) => prev.map((m) => (m.id === id ? Object.assign({}, msg, { id }) : m)));

    // ------------------------------------------------------------------
    // Ask — POST /ai-query/v1/ask { question }
    // ------------------------------------------------------------------
    // Recent completed Q&A turns, so the backend planner can resolve
    // follow-ups ("yes", "just the safety ones"). Nudges, thinking
    // indicators and unanswered turns are excluded.
    const conversationHistory = (msgs) => {
        var turns = [];
        var pendingQ = null;
        (msgs || []).forEach((m) => {
            if (!m || m.skipInPdf || m.kind === 'thinking') return;
            if (m.role === 'user' && m.kind === 'text' && m.text) {
                pendingQ = m.text;
            } else if (m.kind === 'results' && pendingQ) {
                turns.push({
                    question: String(pendingQ).slice(0, 1000),
                    interpretation: String(m.interpretation || '').slice(0, 1500),
                    operation: m.resultKind || null,
                    summary: String(m.summary || '').slice(0, 4000),
                });
                pendingQ = null;
            }
        });
        return turns.slice(-2);
    };

    const sendQuestion = (text) => {
        var question = (text || '').trim();
        if (!question || busy) return;

        setBusy(true);
        setInput('');
        pushMessage({ id: nextId(), role: 'user', kind: 'text', text: question });
        var thinkingId = nextId();
        pushMessage({ id: thinkingId, role: 'assistant', kind: 'thinking' });

        var body = { question };
        if (pendingClarification) {
            body.followUpTo = pendingClarification;
        } else {
            // Not answering a clarification question: give the planner the
            // recent turns so it can resolve follow-ups.
            var hist = conversationHistory(messages);
            if (hist.length > 0) body.history = hist;
        }

        axios.post(AI_QUERY_ASK, body)
            .then((res) => {
                var data = res.data || {};
                if (data.needsClarification) {
                    // The admin's next message answers this — chain it.
                    setPendingClarification(data.requestId || null);
                    replaceMessage(thinkingId, {
                        role: 'assistant', kind: 'clarification',
                        text: data.clarificationQuestion || 'Could you give me a bit more detail?',
                    });
                } else {
                    setPendingClarification(null);
                    replaceMessage(thinkingId, {
                        role: 'assistant', kind: 'plan',
                        requestId: data.requestId,
                        interpretation: data.interpretation,
                        queryPreview: data.queryPreview,
                        expiresAt: data.expiresAt,
                        planState: 'pending',
                    });
                }
            })
            .catch((err) => {
                replaceMessage(thinkingId, {
                    role: 'assistant', kind: 'error',
                    text: getErrorText(err, 'Could not plan that question.'),
                });
            })
            .finally(() => setBusy(false));
    };

    // ------------------------------------------------------------------
    // Confirm — POST /ai-query/v1/:requestId/confirm { confirmed: true }
    // ------------------------------------------------------------------
    const confirmPlan = (msg) => {
        if (busy || !msg || msg.planState !== 'pending') return;
        setBusy(true);
        replaceMessage(msg.id, Object.assign({}, msg, { planState: 'running' }));

        axios.post(aiQueryConfirmUrl(msg.requestId), { confirmed: true })
            .then((res) => {
                var data = res.data || {};
                replaceMessage(msg.id, {
                    role: 'assistant', kind: 'results',
                    requestId: msg.requestId,
                    interpretation: data.interpretation || msg.interpretation,
                    resultKind: data.kind,
                    resultCount: data.resultCount,
                    totalMatches: data.totalMatches,
                    hasMore: data.hasMore === true,
                    nextCursor: data.nextCursor || null,
                    summary: data.summary,
                    results: data.results,
                    groupThemes: data.groupThemes,
                });
                // First-order analysis is in; always end by offering to go deeper.
                pushMessage({ id: nextId(), role: 'assistant', kind: 'text', skipInPdf: true, text: 'Want me to dig deeper into any of this?' });
            })
            .catch((err) => {
                var status = err && err.response && err.response.status;
                if (status === 409 || status === 410 || status === 404) {
                    // Terminal backend states (already handled / expired) —
                    // the plan card can no longer be confirmed.
                    replaceMessage(msg.id, Object.assign({}, msg, {
                        planState: 'stale',
                        planError: getErrorText(err, 'This plan can no longer be confirmed.'),
                    }));
                } else {
                    replaceMessage(msg.id, Object.assign({}, msg, {
                        planState: 'pending',
                        planError: getErrorText(err, 'Query failed to execute.'),
                    }));
                }
            })
            .finally(() => setBusy(false));
    };

    // ------------------------------------------------------------------
    // Load more — GET /ai-query/v1/:requestId/results?cursor=…&limit=20
    // Appends the next page to the existing message list. This never runs a
    // new AI analysis or confirmation: the backend replays the confirmed
    // request's scope, filters and permissions for the same admin/org.
    // ------------------------------------------------------------------
    const loadMoreResults = (msg) => {
        if (!msg || !msg.requestId || msg.loadingMore || !msg.hasMore || !msg.nextCursor) return;
        var id = msg.id;
        replaceMessage(id, Object.assign({}, msg, { loadingMore: true, moreError: null }));

        axios.get(aiQueryResultsUrl(msg.requestId), { params: { cursor: msg.nextCursor, limit: 20 } })
            .then((res) => {
                var data = res.data || {};
                var page = Array.isArray(data.results) ? data.results : [];
                setMessages((prev) => prev.map((m) => {
                    if (m.id !== id) return m;
                    var merged = (Array.isArray(m.results) ? m.results : []).concat(page);
                    return Object.assign({}, m, {
                        results: merged,
                        resultCount: merged.length,
                        totalMatches: data.totalMatches !== undefined ? data.totalMatches : m.totalMatches,
                        hasMore: data.hasMore === true,
                        nextCursor: data.nextCursor || null,
                        loadingMore: false,
                        moreError: null,
                    });
                }));
            })
            .catch((err) => {
                setMessages((prev) => prev.map((m) => (
                    m.id === id
                        ? Object.assign({}, m, {
                            loadingMore: false,
                            moreError: getErrorText(err, 'Could not load more messages.'),
                        })
                        : m
                )));
            });
    };

    // ------------------------------------------------------------------
    // Discard — POST /ai-query/v1/:requestId/confirm { confirmed: false }
    // ------------------------------------------------------------------
    const discardPlan = (msg) => {
        if (busy || !msg || msg.planState !== 'pending') return;
        setBusy(true);
        axios.post(aiQueryConfirmUrl(msg.requestId), { confirmed: false })
            .catch(() => { /* discard is best-effort; the plan simply expires */ })
            .finally(() => {
                replaceMessage(msg.id, {
                    role: 'assistant', kind: 'text',
                    text: 'Plan discarded — nothing was executed.',
                    muted: true,
                });
                setBusy(false);
            });
    };

    // ------------------------------------------------------------------
    // Revise — POST /ai-query/v1/:requestId/confirm { confirmed: false, correction }
    // The backend replans immediately and returns the new PENDING plan.
    // ------------------------------------------------------------------
    const revisePlan = (msg, correction) => {
        var text = (correction || '').trim();
        if (busy || !msg || !text) return;
        setBusy(true);
        replaceMessage(msg.id, Object.assign({}, msg, { planState: 'running', planError: null }));

        axios.post(aiQueryConfirmUrl(msg.requestId), { confirmed: false, correction: text })
            .then((res) => {
                var data = res.data || {};
                if (data.revised) {
                    replaceMessage(msg.id, {
                        role: 'assistant', kind: 'text',
                        text: 'Revised plan requested — showing the new plan below.',
                        muted: true,
                    });
                    pushMessage({
                        id: nextId(), role: 'assistant', kind: 'plan',
                        requestId: data.revised.requestId,
                        interpretation: data.revised.interpretation,
                        queryPreview: data.revised.queryPreview,
                        expiresAt: data.revised.expiresAt,
                        planState: 'pending',
                        supersedesNote: true,
                    });
                } else if (data.needsClarification) {
                    replaceMessage(msg.id, {
                        role: 'assistant', kind: 'clarification',
                        text: data.clarificationQuestion || 'Could you give me a bit more detail?',
                    });
                } else {
                    replaceMessage(msg.id, Object.assign({}, msg, {
                        planState: 'pending',
                        planError: (data.replanError) || 'Could not revise the plan.',
                    }));
                }
            })
            .catch((err) => {
                replaceMessage(msg.id, Object.assign({}, msg, {
                    planState: 'pending',
                    planError: getErrorText(err, 'Could not revise the plan.'),
                }));
            })
            .finally(() => setBusy(false));
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendQuestion(input);
        }
    };

    return (
        <React.Fragment>
            <div
                className={'atv-backdrop' + (open ? ' atv-open' : '')}
                onClick={onClose}
                aria-hidden="true"
            />
            <aside className={'atv-drawer' + (open ? ' atv-open' : '')} aria-hidden={!open}>
                {/* Header */}
                <div className="atv-header">
                    <div className="atv-header-icon"><BiBot size={22} /></div>
                    <div className="atv-header-titles">
                        <div className="atv-title-row">
                            <span className="atv-title">Ask Trivvy</span>
                            <span className="atv-beta">BETA</span>
                        </div>
                        <div className="atv-subtitle">AI analysis of user-initiated chats across hashtags</div>
                    </div>
                    <div className="atv-header-actions">
                        <button className="atv-icon-btn" onClick={downloadSessionPdf} title="Download this session as PDF" disabled={busy || messages.length === 0}>
                            <BiDownload size={20} />
                        </button>
                        <button className="atv-icon-btn" onClick={requestClearConversation} title="Start a new conversation" disabled={busy}>
                            <BiRefresh size={20} />
                        </button>
                        <button className="atv-icon-btn" onClick={onClose} title="Close">
                            <BiX size={22} />
                        </button>
                    </div>
                </div>

                {/* Thread */}
                <div className="atv-body" ref={bodyRef}>
                    {messages.length === 0 && (
                        <React.Fragment>
                            <div className="atv-greeting">
                                <div className="atv-greeting-title">
                                    <span className="atv-sparkle">✦</span>
                                    <span>Hi {firstName || 'there'} — I've read across your user chats.</span>
                                </div>
                                <div className="atv-greeting-sub">
                                    Ask me anything about your user-initiated chats, or start with one of these:
                                </div>
                            </div>
                            <div className="atv-suggestions">
                                {SUGGESTIONS.map((s) => (
                                    <button key={s} className="atv-suggestion" onClick={() => sendQuestion(s)} disabled={busy}>
                                        <span>{s}</span>
                                        <BiChevronRight size={20} className="atv-suggestion-chevron" />
                                    </button>
                                ))}
                            </div>
                        </React.Fragment>
                    )}

                    {messages.map((m) => (
                        <MessageRow key={m.id} msg={m} busy={busy}
                            onConfirm={() => confirmPlan(m)}
                            onDiscard={() => discardPlan(m)}
                            onRevise={(correction) => revisePlan(m, correction)}
                            onLoadMore={() => loadMoreResults(m)} />
                    ))}
                </div>

                {/* Composer */}
                <div className="atv-footer">
                    <div className="atv-composer">
                        <input
                            ref={inputRef}
                            className="atv-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleInputKeyDown}
                            placeholder="Ask about your chats..."
                            disabled={busy}
                            maxLength={1000}
                        />
                        <button
                            className={'atv-send' + ((!input.trim() || busy) ? ' atv-send-disabled' : '')}
                            onClick={() => sendQuestion(input)}
                            disabled={!input.trim() || busy}
                            title="Send"
                        >
                            <BsSend size={16} />
                        </button>
                    </div>
                    <div className="atv-disclaimer">
                        Ask Trivvy analyzes chat content across your hashtags. Responses are generated by AI and should be reviewed.
                    </div>
                </div>
                {confirmingClear && (
                    <div className="atv-confirm-overlay" onClick={() => setConfirmingClear(false)}>
                        <div className="atv-confirm-box" role="alertdialog" aria-modal="true" aria-label="Clear chat confirmation" onClick={(e) => e.stopPropagation()}>
                            <div className="atv-confirm-title">Start a new conversation?</div>
                            <div className="atv-confirm-text">This will clear the current chat.</div>
                            <div className="atv-confirm-actions">
                                <button className="atv-btn-ghost" onClick={() => setConfirmingClear(false)}>Cancel</button>
                                <button className="atv-btn-primary" onClick={clearConversation}>Clear chat</button>
                            </div>
                        </div>
                    </div>
                )}
            </aside>
        </React.Fragment>
    );
};

// ---------------------------------------------------------------------------
// One row of the thread.
// ---------------------------------------------------------------------------
const MessageRow = ({ msg, busy, onConfirm, onDiscard, onRevise, onLoadMore }) => {
    if (msg.role === 'user') {
        return (
            <div className="atv-row atv-row-user">
                <div className="atv-bubble atv-bubble-user">{msg.text}</div>
            </div>
        );
    }
    if (msg.kind === 'thinking') {
        return (
            <div className="atv-row">
                <div className="atv-thinking"><span /><span /><span /></div>
            </div>
        );
    }
    if (msg.kind === 'error') {
        return (
            <div className="atv-row">
                <div className="atv-error">{msg.text}</div>
            </div>
        );
    }
    if (msg.kind === 'clarification') {
        return (
            <div className="atv-row">
                <div className="atv-clarification">
                    <div className="atv-clarification-label">I need a bit more detail</div>
                    <div>{msg.text}</div>
                </div>
            </div>
        );
    }
    if (msg.kind === 'plan') {
        return <PlanCard msg={msg} busy={busy} onConfirm={onConfirm} onDiscard={onDiscard} onRevise={onRevise} />;
    }
    if (msg.kind === 'results') {
        return <ResultsCard msg={msg} onLoadMore={onLoadMore} />;
    }
    // plain assistant text (discard / revision notes)
    return (
        <div className="atv-row">
            <div className={'atv-note' + (msg.muted ? ' atv-note-muted' : '')}>{msg.text}</div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Plan preview card — the human-in-the-loop gate. Nothing runs until Confirm.
// ---------------------------------------------------------------------------
const PlanCard = ({ msg, busy, onConfirm, onDiscard, onRevise }) => {
    const [showCorrection, setShowCorrection] = useState(false);
    const [correction, setCorrection] = useState('');
    const state = msg.planState || 'pending';
    const interactive = state === 'pending' && !busy;
    const expiresLabel = msg.expiresAt
        ? 'Plan expires ' + new Date(msg.expiresAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
        : '';

    return (
        <div className="atv-row">
            <div className={'atv-plan' + (state === 'stale' ? ' atv-plan-stale' : '')}>
                {msg.supersedesNote && (
                    <div className="atv-plan-revised-flag">Revised plan</div>
                )}
                <div className="atv-plan-kicker">Quick check before I run this</div>
                {msg.interpretation && (
                    <div className="atv-plan-interpretation">{msg.interpretation}</div>
                )}
                {expiresLabel && <div className="atv-plan-expires">{expiresLabel}</div>}
                {msg.planError && <div className="atv-plan-error">{msg.planError}</div>}

                {state === 'stale' ? (
                    <div className="atv-plan-stale-note">This plan is no longer active. Ask again to get a fresh plan.</div>
                ) : state === 'running' ? (
                    <div className="atv-plan-running">Working on it…</div>
                ) : (
                    <div className="atv-plan-actions">
                        <button className="atv-btn-primary" onClick={onConfirm} disabled={!interactive}>
                            Confirm &amp; run
                        </button>
                        <button className="atv-btn-ghost" onClick={onDiscard} disabled={!interactive}>
                            Discard
                        </button>
                    </div>
                )}

                {state === 'pending' && (
                    <div className="atv-correction">
                        {!showCorrection ? (
                            <button className="atv-correction-toggle" onClick={() => setShowCorrection(true)} disabled={busy}>
                                Suggest a correction
                            </button>
                        ) : (
                            <div className="atv-correction-row">
                                <input
                                    className="atv-correction-input"
                                    value={correction}
                                    onChange={(e) => setCorrection(e.target.value)}
                                    placeholder="e.g. only the last 7 days…"
                                    disabled={busy}
                                    maxLength={500}
                                />
                                <button
                                    className="atv-btn-ghost atv-btn-small"
                                    onClick={() => { onRevise(correction); setCorrection(''); setShowCorrection(false); }}
                                    disabled={!correction.trim() || busy}
                                >
                                    Revise
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Minimal markdown rendering for LLM-generated text (themes summaries).
// Dependency-free and built as React elements (no innerHTML), so untrusted
// text stays safe. Supports ### headings, "- " bullet lists, **bold**,
// and plain paragraphs.
// ---------------------------------------------------------------------------
const renderInlineMd = (text, keyPrefix) => {
    var parts = String(text).split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
        var m = /^\*\*([^*]+)\*\*$/.exec(p);
        return m
            ? <strong key={keyPrefix + '-' + i}>{m[1]}</strong>
            : <React.Fragment key={keyPrefix + '-' + i}>{p}</React.Fragment>;
    });
};

const renderMarkdown = (text) => {
    var lines = String(text || '').split('\n');
    var blocks = [];
    var listItems = [];
    const flushList = () => {
        if (listItems.length) {
            blocks.push(<ul key={'ul-' + blocks.length} className="atv-md-list">{listItems}</ul>);
            listItems = [];
        }
    };
    lines.forEach((raw, idx) => {
        var line = raw.trim();
        if (!line) { flushList(); return; }
        var h = /^#{1,3}\s*(.+)$/.exec(line);
        if (h) {
            flushList();
            blocks.push(<div key={'h-' + idx} className="atv-md-h">{renderInlineMd(h[1], 'h' + idx)}</div>);
            return;
        }
        var li = /^[-*]\s+(.+)$/.exec(line);
        if (li) {
            listItems.push(<li key={'li-' + idx}>{renderInlineMd(li[1], 'li' + idx)}</li>);
            return;
        }
        flushList();
        blocks.push(<p key={'p-' + idx} className="atv-md-p">{renderInlineMd(line, 'p' + idx)}</p>);
    });
    flushList();
    return blocks;
};

// ---------------------------------------------------------------------------
// Executed result card. Message searches are paged by the backend: the first
// page arrives with the confirm response; further pages load via
// GET /ai-query/v1/:requestId/results with the returned cursor — no new AI
// analysis or confirmation. Pages are appended in the backend's
// chronological order.
// ---------------------------------------------------------------------------
const ResultsCard = ({ msg, onLoadMore }) => {
    var kindLabel = msg.resultKind === 'count' ? 'Count'
        : msg.resultKind === 'themes' ? 'Themes'
        : msg.resultKind === 'documents' ? 'Messages' : 'Result';
    var docs = Array.isArray(msg.results) ? msg.results : [];
    var isMessages = msg.resultKind === 'documents';

    var countLabel = null;
    if (isMessages && docs.length > 0) {
        if (typeof msg.totalMatches === 'number') {
            countLabel = 'Showing ' + docs.length + ' of ' + msg.totalMatches + ' messages';
        } else {
            // Semantic searches (and timed-out counts) report totalMatches: null —
            // the full matching total is unknown.
            countLabel = 'Showing ' + docs.length + ' messages (full total unknown)';
        }
    }

    return (
        <div className="atv-row">
            <div className="atv-results">
                <div className="atv-results-kicker">
                    <span className="atv-results-badge">{kindLabel}</span>
                </div>
                {msg.summary && <div className="atv-results-summary">{renderMarkdown(msg.summary)}</div>}

                {countLabel && <div className="atv-results-count">{countLabel}</div>}

                {isMessages && docs.length > 0 && (
                    <div className="atv-msg-list">
                        {docs.map((d, i) => (
                            <MessageItem key={(d && (d._id || d.id)) || i} d={d} />
                        ))}
                    </div>
                )}

                {isMessages && msg.hasMore && msg.nextCursor && (
                    <button
                        className="atv-btn-ghost atv-load-more"
                        onClick={() => onLoadMore(msg)}
                        disabled={!!msg.loadingMore}
                    >
                        {msg.loadingMore ? 'Loading…' : 'Load more messages'}
                    </button>
                )}

                {isMessages && msg.moreError && (
                    <div className="atv-more-error">
                        <div className="atv-plan-error">{msg.moreError}</div>
                        <button
                            className="atv-btn-ghost atv-btn-small"
                            onClick={() => onLoadMore(msg)}
                            disabled={!!msg.loadingMore}
                        >
                            Retry
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// One message: text, sender full name (or N/A), date, available sentiment.
const MessageItem = ({ d }) => {
    var sender = (d && d.senderName) ? String(d.senderName) : 'N/A';
    var date = '';
    try { if (d && d.createdAt) date = new Date(d.createdAt).toLocaleString(); } catch (e) { /* ignore */ }
    var sentiment = (d && d.textSentimentsData && d.textSentimentsData.sentiment)
        ? String(d.textSentimentsData.sentiment).toLowerCase() : null;
    return (
        <div className="atv-msg">
            <div className="atv-msg-body">{(d && d.body) || '(no text)'}</div>
            <div className="atv-msg-meta">
                <span className="atv-msg-sender">{sender}</span>
                {date ? <span className="atv-msg-date">{date}</span> : null}
                {sentiment ? <span className="atv-msg-sentiment">{sentiment}</span> : null}
                {d && d.currentGroupName ? <span className="atv-msg-group">#{d.currentGroupName}</span> : null}
            </div>
        </div>
    );
};

// Compact one-doc rendering: prefer readable fields over a raw dump.
// Used by the PDF export; the drawer itself renders MessageItem above.
const formatDoc = (d) => {
    if (!d || typeof d !== 'object') return String(d);
    var lines = [];
    if (d.body) lines.push(String(d.body));
    var meta = [];
    meta.push(d.senderName ? String(d.senderName) : 'N/A');
    if (d.currentGroupName) meta.push('#' + d.currentGroupName);
    if (d.createdAt) {
        try { meta.push(new Date(d.createdAt).toLocaleString()); } catch (e) { /* ignore */ }
    }
    if (d.textSentimentsData && d.textSentimentsData.sentiment) meta.push(String(d.textSentimentsData.sentiment).toLowerCase());
    if (meta.length > 0) lines.push(meta.join(' · '));
    if (lines.length === 0) {
        var raw = JSON.stringify(d);
        return raw.length > 300 ? raw.slice(0, 300) + '…' : raw;
    }
    return lines.join('\n');
};

// ---------------------------------------------------------------------------
// Session PDF export — direct text generation with jsPDF (page-by-page).
// No canvas rasterization: fast, no page freeze, selectable/searchable text,
// any session size. Requires the `jspdf` npm package.
// ---------------------------------------------------------------------------
const PDF_MARGIN = 48;

const pdfInlineRuns = (text) => {
    var runs = [];
    String(text == null ? '' : text).split(/(\*\*[^*]+\*\*)/g).forEach((p) => {
        if (!p) return;
        var m = /^\*\*([^*]+)\*\*$/.exec(p);
        if (m) runs.push({ text: m[1], bold: true });
        else runs.push({ text: p, bold: false });
    });
    return runs;
};

// Wrap styled runs into lines that fit maxW (points). Returns an array of
// lines; each line is an array of runs.
const pdfWrapRuns = (doc, runs, maxW, size) => {
    var tokens = [];
    const measure = (t, bold) => {
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        doc.setFontSize(size);
        return doc.getTextWidth(t);
    };
    runs.forEach((run) => {
        String(run.text).split(/(\s+)/).forEach((tok) => {
            if (!tok) return;
            if (/^\s+$/.test(tok)) { tokens.push({ text: ' ', bold: run.bold, space: true }); return; }
            var t = tok;
            while (t && measure(t, run.bold) > maxW) {
                var cut = 1;
                while (cut < t.length && measure(t.slice(0, cut + 1), run.bold) <= maxW) cut++;
                tokens.push({ text: t.slice(0, cut), bold: run.bold });
                t = t.slice(cut);
            }
            if (t) tokens.push({ text: t, bold: run.bold });
        });
    });
    var lines = [], cur = [], curW = 0;
    tokens.forEach((tok) => {
        if (tok.space) {
            if (curW === 0) return;
            var sw = measure(' ', tok.bold);
            if (curW + sw > maxW) { lines.push(cur); cur = []; curW = 0; return; }
            var last = cur[cur.length - 1];
            if (last && last.bold === tok.bold) last.text += ' ';
            else cur.push({ text: ' ', bold: tok.bold });
            curW += sw;
            return;
        }
        var tw = measure(tok.text, tok.bold);
        if (curW + tw > maxW && curW > 0) { lines.push(cur); cur = []; curW = 0; }
        cur.push({ text: tok.text, bold: tok.bold });
        curW += tw;
    });
    if (cur.length) lines.push(cur);
    return lines;
};

const pdfEnsureSpace = (ctx, needed) => {
    if (ctx.y + needed > ctx.pageH - PDF_MARGIN) {
        ctx.doc.addPage();
        ctx.y = PDF_MARGIN;
    }
};

// Draw one markdown-ish paragraph: runs with {size, indent, bullet, gapAfter}.
const pdfDrawBlock = (ctx, runs, opts) => {
    opts = opts || {};
    var size = opts.size || 11;
    var indent = opts.indent || 0;
    var bulletW = opts.bullet ? 14 : 0;
    var maxW = ctx.pageW - PDF_MARGIN * 2 - indent - bulletW;
    var lines = pdfWrapRuns(ctx.doc, runs, maxW, size);
    var lineH = size * 1.35;
    lines.forEach((lineRuns, li) => {
        pdfEnsureSpace(ctx, lineH);
        if (opts.bullet && li === 0) {
            ctx.doc.setFont('helvetica', 'normal');
            ctx.doc.setFontSize(size);
            ctx.doc.setTextColor(15, 23, 42);
            ctx.doc.text('•', PDF_MARGIN + indent, ctx.y);
        }
        ctx.doc.setTextColor.apply(ctx.doc, opts.color || [15, 23, 42]);
        var cx = PDF_MARGIN + indent + bulletW;
        lineRuns.forEach((run) => {
            ctx.doc.setFont('helvetica', run.bold ? 'bold' : 'normal');
            ctx.doc.setFontSize(size);
            ctx.doc.text(run.text, cx, ctx.y);
            cx += ctx.doc.getTextWidth(run.text);
        });
        ctx.y += lineH;
    });
    ctx.y += (opts.gapAfter != null ? opts.gapAfter : 6);
};

const pdfDrawLabel = (ctx, text) => {
    pdfEnsureSpace(ctx, 20);
    ctx.doc.setFont('helvetica', 'bold');
    ctx.doc.setFontSize(12);
    ctx.doc.setTextColor(124, 45, 18);
    ctx.doc.text(text, PDF_MARGIN, ctx.y);
    ctx.y += 16;
};

const pdfDrawSeparator = (ctx) => {
    pdfEnsureSpace(ctx, 16);
    ctx.doc.setDrawColor(226, 232, 240);
    ctx.doc.setLineWidth(0.5);
    ctx.doc.line(PDF_MARGIN, ctx.y, ctx.pageW - PDF_MARGIN, ctx.y);
    ctx.y += 14;
};

const buildSessionPdf = (messages) => {
    var doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
    var ctx = {
        doc,
        y: PDF_MARGIN,
        pageW: doc.internal.pageSize.getWidth(),
        pageH: doc.internal.pageSize.getHeight(),
    };
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(15, 23, 42);
    doc.text('Ask Trivvy — chat session', PDF_MARGIN, ctx.y);
    ctx.y += 22;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text('Exported ' + new Date().toLocaleString(), PDF_MARGIN, ctx.y);
    ctx.y += 24;

    (messages || []).forEach((m) => {
        if (!m || m.kind === 'thinking' || m.skipInPdf) return;
        if (m.role === 'user') {
            pdfDrawLabel(ctx, 'You asked');
            pdfDrawBlock(ctx, pdfInlineRuns(m.text), {});
        } else if (m.kind === 'text') {
            pdfDrawLabel(ctx, 'Trivvy');
            pdfDrawBlock(ctx, pdfInlineRuns(m.text), {});
        } else if (m.kind === 'clarification') {
            pdfDrawLabel(ctx, 'Trivvy asked');
            pdfDrawBlock(ctx, pdfInlineRuns(m.text), {});
        } else if (m.kind === 'plan') {
            var state = m.planState === 'stale' ? ' (expired)' : m.planState === 'running' ? ' (running)' : ' (not run)';
            pdfDrawLabel(ctx, 'Planned query' + state);
            pdfDrawBlock(ctx, pdfInlineRuns(m.interpretation), {});
        } else if (m.kind === 'results') {
            var kindLabel = m.resultKind === 'count' ? 'Count result'
                : m.resultKind === 'themes' ? 'Themes result'
                : m.resultKind === 'documents' ? 'Messages' : 'Result';
            pdfDrawLabel(ctx, kindLabel);
            if (m.interpretation) pdfDrawBlock(ctx, pdfInlineRuns(m.interpretation), {});
            if (m.summary) {
                String(m.summary).split('\n').forEach((raw) => {
                    var line = raw.trim();
                    if (!line) return;
                    var h = /^#{1,3}\s*(.+)$/.exec(line);
                    var li = /^[-*]\s+(.+)$/.exec(line);
                    if (h) pdfDrawBlock(ctx, pdfInlineRuns(h[1]), { size: 13, gapAfter: 4 });
                    else if (li) pdfDrawBlock(ctx, pdfInlineRuns(li[1]), { bullet: true, indent: 8 });
                    else pdfDrawBlock(ctx, pdfInlineRuns(line), {});
                });
            }
            var docs = Array.isArray(m.results) ? m.results : [];
            docs.forEach((d) => {
                String(formatDoc(d)).split('\n').forEach((ln) => {
                    if (ln.trim()) pdfDrawBlock(ctx, pdfInlineRuns(ln), { size: 10, indent: 8, gapAfter: 3, color: [71, 85, 105] });
                });
                ctx.y += 5;
            });
        } else if (m.kind === 'error') {
            pdfDrawLabel(ctx, 'Error');
            pdfDrawBlock(ctx, pdfInlineRuns(m.text), {});
        }
        pdfDrawSeparator(ctx);
    });

    var pages = doc.internal.getNumberOfPages();
    for (var i = 1; i <= pages; i++) {
        doc.setPage(i);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(100, 116, 139);
        doc.text('Page ' + i + ' of ' + pages, ctx.pageW - PDF_MARGIN, ctx.pageH - 24, { align: 'right' });
    }
    doc.save('ask-trivvy-session-' + new Date().toISOString().slice(0, 10) + '.pdf');
};

export default AskTrivvyDrawer;
